import { ChangeDetectorRef, Component, Inject, PLATFORM_ID, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { RouterModule } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FoMaUserModel } from '../../models/foma-user.model';
import { UserListFilterComponent } from '../user-list-filter/user-list-filter.component';
import { UserListService } from '../../../../core/services/user-list/user-list.service';
import { BehaviorSubject, map, Observable, Subject, takeUntil } from 'rxjs';
import { ListFilter } from '../../../../core/models/list-filter';
import { ColumnManagerService } from '../../../../core/services/column-manager/column-manager.service';
import { SortCoordinationService } from '../../../../core/services/sort-coordination/sort-coordination.service';

@Component({
  selector: 'app-user-list',
  imports: [MatTableModule, MatPaginatorModule, MatSortModule, RouterModule, MatIconModule, MatButtonModule, UserListFilterComponent],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss'
})
export class UserListComponent {
  private readonly ngUnsubscribe$ = new Subject<void>();
  private readonly tableId = 'user-table';

  users: FoMaUserModel[] = [];
  protected filteredUserList$: Observable<MatTableDataSource<FoMaUserModel>> | undefined;
  usersDataSource = new MatTableDataSource<FoMaUserModel>;

  isLoading = true;
  displayedColumns: (string | undefined)[] = ["userId", "displayName", "rolesText"];
  skeletonRows = Array(20).fill(0);

  data$!: BehaviorSubject<FoMaUserModel[] | null>;

  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
  @ViewChild(MatSort) sort: MatSort | undefined;

  isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private userListService: UserListService, private changeDetectorRef: ChangeDetectorRef, private columnManagerService: ColumnManagerService, private sortCoordinationService: SortCoordinationService) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private initSubscriptions = (): void => {
    this.filteredUserList$ = this.userListService.filterChange.pipe(takeUntil(this.ngUnsubscribe$))
      .pipe(
        map((value) => {
          return this.applyFilter(
            value || ""
          );
        })
      );
  }

  async ngOnInit(): Promise<void> {
    this.userListService.loadFoMaUsers();
    this.data$ = this.userListService.data$;
    this.data$.subscribe(users => {
      if (users) {
        this.users = users;
        this.usersDataSource.data = users;
        this.isLoading = false;
      }
    })
    this.initSubscriptions();

    this.sortCoordinationService.clearSort$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(tableId => {
        if (tableId === this.tableId && this.sort) {
          this.sort.active = '';
          this.sort.direction = '';
          this.columnManagerService.clearSortState(this.tableId);
          this.sort.sortChange.emit({ active: '', direction: '' });
        }
      });
  }

  async ngAfterViewInit(): Promise<void> {
    this.usersDataSource.paginator = this.paginator!;
    this.usersDataSource.sort = this.sort!;

    this.userListService.filterChange.subscribe(filter => {
      this.applyFilter(filter);
      this.changeDetectorRef.detectChanges();
    });

    const savedPageSize = this.columnManagerService.loadPageSize(this.tableId);
    if (savedPageSize && this.paginator) {
      this.paginator.pageSize = savedPageSize;
    }

    this.paginator?.page.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((pageEvent) => {
      this.columnManagerService.savePageSize(this.tableId, pageEvent.pageSize);
    });

    const savedSort = this.columnManagerService.loadSortState(this.tableId);
    if (savedSort && this.sort) {
      this.sort.active = savedSort.active;
      this.sort.direction = savedSort.direction;
      this.sort.sortChange.emit({ active: savedSort.active, direction: savedSort.direction });
    }

    this.sort?.sortChange.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      if (this.sort) {
        this.columnManagerService.saveSortState(this.tableId, this.sort);
      }
    });
  }

  applyFilter(filterValues: ListFilter) {
    let users = this.data$.value!;
    if (!users) {
      return this.usersDataSource;
    }

    if (filterValues?.search.length) {
      users = users.filter((user) => this.checkNestedObject(user, filterValues.search[0].value, filterValues.search[0].activated))
    }

    this.usersDataSource.data = users;

    if (this.paginator) this.usersDataSource.paginator = this.paginator;
    if (this.sort) this.usersDataSource.sort = this.sort;

    return this.usersDataSource;
  }

  checkNestedObject = (obj: any, filter: string, activated: boolean = true): boolean => {
    if (!activated) {
      return true;
    }

    return Object.keys(obj).some(key => {
      const value = obj[key];
      if (value && typeof value === 'object') {
        return this.checkNestedObject(value, filter);
      }
      const lowerCaseFilter = filter.toLowerCase();
      return value && value.toString().toLowerCase().includes(lowerCaseFilter);
    });
  };
}
